import { baseApi } from './baseApi';
import { Attribute } from 'models';
import { CheckUsedResponse, QueryParams, ReplaceRequest, ReplaceResponse, TAG_TYPES } from 'types';
import { getQueryParams } from 'utils';

const URL_ATTRIBUTE = 'attributes';

const attributeApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAttributes: builder.query<
      {
        data: {
          rows: Attribute[];
          count: number;
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const queryParams = params?.query || params;
        const query = getQueryParams(queryParams);
        if (query === '') return { url: URL_ATTRIBUTE, method: 'GET' };
        return {
          url: `${URL_ATTRIBUTE}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...(result?.data.rows || []).map(({ id }) => ({ type: TAG_TYPES.Attribute, id })),
              { type: TAG_TYPES.Attribute, id: 'LIST' },
            ]
          : [{ type: TAG_TYPES.Attribute, id: 'LIST' }];
      },
    }),

    getAttributeById: builder.query<Attribute, string>({
      query: (id) => ({
        url: `${URL_ATTRIBUTE}/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: TAG_TYPES.Attribute, id }],
    }),

    createAttribute: builder.mutation<Attribute, Omit<Attribute, 'id'>>({
      query: (data) => ({
        url: URL_ATTRIBUTE,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.Attribute, id: 'LIST' }],
    }),

    updateAttribute: builder.mutation<Attribute, Partial<Attribute> & { id: string }>({
      query: ({ id, ...patch }) => ({
        url: `${URL_ATTRIBUTE}/${id}`,
        method: 'PUT',
        data: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Attribute, id },
        { type: TAG_TYPES.Attribute, id: 'LIST' },
      ],
    }),

    deleteAttribute: builder.mutation<void, string>({
      query: (id) => ({
        url: `${URL_ATTRIBUTE}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: TAG_TYPES.Attribute, id },
        { type: TAG_TYPES.Attribute, id: 'LIST' },
      ],
    }),

    // Thêm API check usage cho Attribute
    checkAttributeUsage: builder.query<CheckUsedResponse, string>({
      query: (id) => ({
        url: `${URL_ATTRIBUTE}/${id}/check-used`,
        method: 'POST',
      }),
      providesTags: (result, error, id) => [{ type: TAG_TYPES.Attribute, id: `CHECK_USED_${id}` }],
    }),

    // Thêm API deactivate với thay thế cho Attribute
    deactivateAttribute: builder.mutation<ReplaceResponse, { id: string; payload: ReplaceRequest }>({
      query: ({ id, payload }) => ({
        url: `${URL_ATTRIBUTE}/${id}/deactivate`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Attribute, id },
        { type: TAG_TYPES.Attribute, id: 'LIST' },
        { type: TAG_TYPES.Product, id: 'LIST' },
        { type: TAG_TYPES.Category, id: 'LIST' },
      ],
    }),

    // Thêm API delete with replacement cho Attribute
    deleteAttributeWithReplacement: builder.mutation<ReplaceResponse, { id: string; payload: ReplaceRequest }>({
      query: ({ id, payload }) => ({
        url: `${URL_ATTRIBUTE}/${id}/delete`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Attribute, id },
        { type: TAG_TYPES.Attribute, id: 'LIST' },
        { type: TAG_TYPES.Product, id: 'LIST' },
        { type: TAG_TYPES.Category, id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAttributesQuery: useGetAllAttributesQuery,
  useLazyGetAttributesQuery: useLazyGetAllAttributesQuery,
  useGetAttributeByIdQuery,
  useCreateAttributeMutation,
  useUpdateAttributeMutation,
  useDeleteAttributeMutation,
  useLazyCheckAttributeUsageQuery,
  useDeactivateAttributeMutation,
  useDeleteAttributeWithReplacementMutation,
} = attributeApiService;
